export type DiagnosticFormName =
  | 'entity-editor'
  | 'add-holdings-selection'
  | 'add-holdings-assets';

// Bound retained timestamps even when updates arrive faster than the window expires.
const RECENT_UPDATE_LIMIT = 64;
const UPDATE_WINDOW_MS = 1000;

function getFieldCategory(name?: string) {
  if (name === 'basicInformationSubform.description') return 'description';
  if (name === 'holdingsGroupId') return 'holdings-group';
  if (name === 'holdingType') return 'holding-type';
  if (/^structuredAssetsSubform\.assets\.\d+\.title$/.test(name ?? '')) {
    return 'asset-title';
  }
  if (/^structuredAssetsSubform\.assets\.\d+\.value$/.test(name ?? '')) {
    return 'asset-value';
  }
  return 'other';
}

interface FormDiagnostics {
  name: DiagnosticFormName;
  mountedAt: number;
  updateCount: number;
  lastUpdateAt: number | null;
  lastField: ReturnType<typeof getFieldCategory> | null;
  recentUpdates: number[];
  getSubscriptionCount: () => number;
  getRenderCount: () => number;
}

const activeForms = new Set<FormDiagnostics>();

/** Records counts and allowlisted field categories, never form values. */
export function registerFormDiagnostics(
  name: DiagnosticFormName,
  getSubscriptionCount: () => number,
  getRenderCount: () => number
) {
  const form: FormDiagnostics = {
    name,
    mountedAt: performance.now(),
    updateCount: 0,
    lastUpdateAt: null,
    lastField: null,
    recentUpdates: [],
    getSubscriptionCount,
    getRenderCount,
  };
  activeForms.add(form);

  return {
    recordUpdate(fieldName?: string) {
      const now = performance.now();
      form.updateCount++;
      form.lastUpdateAt = now;
      form.lastField = getFieldCategory(fieldName);
      form.recentUpdates.push(now);
      if (form.recentUpdates.length > RECENT_UPDATE_LIMIT) {
        form.recentUpdates.shift();
      }
    },
    unregister() {
      activeForms.delete(form);
    },
  };
}

/** Returns browser-only metadata for the forms that opted into diagnostics. */
export function getFormDiagnostics() {
  if (typeof window === 'undefined' || activeForms.size === 0) return null;

  const now = performance.now();
  return {
    active_field: getFieldCategory(
      document.activeElement?.getAttribute('name') ?? undefined
    ),
    forms: Array.from(activeForms, (form) => {
      const recentUpdateCount = form.recentUpdates.filter(
        (time) => now - time < UPDATE_WINDOW_MS
      ).length;
      return {
        name: form.name,
        mounted_ms: Math.round(now - form.mountedAt),
        // Counts committed renders of the form owner, not its field components.
        render_count: form.getRenderCount(),
        // Includes the single subscriber that records these diagnostics.
        subscription_count: form.getSubscriptionCount(),
        update_count: form.updateCount,
        last_update_ms_ago:
          form.lastUpdateAt === null
            ? null
            : Math.round(now - form.lastUpdateAt),
        last_field: form.lastField,
        updates_in_last_second: recentUpdateCount,
        recent_updates_capped: recentUpdateCount === RECENT_UPDATE_LIMIT,
      };
    }),
  };
}
